import React, { useState, useEffect } from 'react';
import { formatHours, formatPremiumHours } from '../../utils/formatHours';
import AnalyticsAPI from '../../services/api/analytics-api';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import TimecardCorrectionModal from './TimecardCorrectionModal';
import {
  addCalendarDays,
  getCurrentWeekMondayYmd,
  parseLocalCalendarDate,
} from '../../utils/field-start-date-policy';
import CorrectionHistorySection from './CorrectionHistorySection';

/**
 * PackageLaborDetail
 * Rendered by Syncfusion's detailTemplate — fetches and displays
 * an employee × weekday hours table inline below the expanded grid row.
 */
const PackageLaborDetail = ({
  packageId,
  packageName,
  projectId,
  weekStart,
  token,
  isAdmin,
  refreshKey = 0,
  onCorrectionSuccess,
}) => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Correction Mode state
  const [correctionMode, setCorrectionMode] = useState(false);
  const [detailedRows, setDetailedRows] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedCells, setSelectedCells] = useState({}); // { "empId|date": [timecard_ids...] }
  const [showCorrectionModal, setShowCorrectionModal] = useState(false);
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);

  useEffect(() => {
    if (!packageId || !weekStart || !token) return;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await AnalyticsAPI.getPackageLaborDetail(
          packageId,
          weekStart,
          token
        );
        setEmployees(data.employees || []);
      } catch (err) {
        setError('Failed to load employee detail');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [packageId, weekStart, token, refreshKey]);

  const isPastWeek = (startStr) => startStr < getCurrentWeekMondayYmd();

  const handleEnterCorrectionMode = async () => {
    if (!projectId) {
      setError('Project ID is missing, cannot enter correction mode.');
      return;
    }
    setDetailLoading(true);
    setCorrectionMode(true);
    setSelectedCells({});
    try {
      const data = await AnalyticsAPI.getTimecardsDetail(
        packageId,
        projectId,
        weekStart,
        token
      );
      setDetailedRows(data.employees || []);
    } catch (err) {
      setError('Failed to load detailed timecards for correction');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCancelCorrectionMode = () => {
    setCorrectionMode(false);
    setSelectedCells({});
  };

  const handleCorrectionSuccess = () => {
    // Clear out correction mode automatically after a successful move
    handleCancelCorrectionMode();
    setHistoryRefreshKey((k) => k + 1);
    if (onCorrectionSuccess) {
      onCorrectionSuccess();
    }
  };

  const currentData = correctionMode ? detailedRows : employees;

  const weekDays = [];
  for (let i = 0; i < 7; i++) {
    const date = addCalendarDays(weekStart, i);
    weekDays.push({
      date,
      label: parseLocalCalendarDate(date).toLocaleDateString('en-US', {
        weekday: 'short',
      }),
    });
  }

  const getDayHours = (emp, dateStr) => {
    const entries = (emp.daily || []).filter((d) => d.date === dateStr);

    let totalSt = 0,
      totalOt = 0,
      totalDt = 0;
    const timecardRows = [];

    for (const e of entries) {
      if (e.timecard_rows) {
        // detail mode structure
        e.timecard_rows.forEach((r) => {
          totalSt += Number(r.hours_st || 0);
          totalOt += Number(r.hours_ot || 0);
          totalDt += Number(r.hours_dt || 0);
          timecardRows.push(r);
        });
      } else {
        // summary mode structure
        totalSt += Number(e.hours_st || 0);
        totalOt += Number(e.hours_ot || 0);
        totalDt += Number(e.hours_dt || 0);
      }
    }

    return {
      total: totalSt + totalOt + totalDt,
      st: totalSt,
      ot: totalOt,
      dt: totalDt,
      timecardRows,
    };
  };

  // Checkbox interactions
  const toggleCell = (empId, dateStr, timecardRowsArr) => {
    if (timecardRowsArr.length === 0) return;
    const key = `${empId}|${dateStr}`;
    setSelectedCells((prev) => {
      const next = { ...prev };
      if (next[key]) {
        delete next[key];
      } else {
        next[key] = timecardRowsArr.map((r) => r.id);
      }
      return next;
    });
  };

  const toggleEmployeeRow = (emp) => {
    const activeDays = weekDays
      .map((d) => ({ date: d.date, hrs: getDayHours(emp, d.date) }))
      .filter((d) => d.hrs.total > 0);
    if (activeDays.length === 0) return;

    let allSelected = true;
    for (const d of activeDays) {
      if (!selectedCells[`${emp.employee_id}|${d.date}`]) {
        allSelected = false;
        break;
      }
    }

    setSelectedCells((prev) => {
      const next = { ...prev };
      if (allSelected) {
        // Deselect all
        for (const d of activeDays) {
          delete next[`${emp.employee_id}|${d.date}`];
        }
      } else {
        // Select all
        for (const d of activeDays) {
          next[`${emp.employee_id}|${d.date}`] = d.hrs.timecardRows.map(
            (r) => r.id
          );
        }
      }
      return next;
    });
  };

  const getEmployeeSelectionState = (emp) => {
    const activeDays = weekDays
      .map((d) => ({ date: d.date, hrs: getDayHours(emp, d.date) }))
      .filter((d) => d.hrs.total > 0);
    if (activeDays.length === 0)
      return { checked: false, indeterminate: false };

    let selectedCount = 0;
    for (const d of activeDays) {
      if (selectedCells[`${emp.employee_id}|${d.date}`]) {
        selectedCount++;
      }
    }

    if (selectedCount === 0) return { checked: false, indeterminate: false };
    if (selectedCount === activeDays.length)
      return { checked: true, indeterminate: false };
    return { checked: false, indeterminate: true };
  };

  const totalSelectedCellsCount = Object.keys(selectedCells).length;

  return (
    <div
      style={{
        backgroundColor: '#f8fafc',
        padding: '16px 20px',
        borderTop: '1px solid #e2e8f0',
        borderLeft: correctionMode ? '3px solid #f59e0b' : undefined,
        transition: 'border-left 0.15s ease',
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
      >
        <Typography variant="subtitle2" color="textSecondary">
          Employee Timecards
        </Typography>
        {isAdmin && isPastWeek(weekStart) && !correctionMode && (
          <Button
            variant="outlined"
            size="small"
            color="primary"
            onClick={handleEnterCorrectionMode}
          >
            Correct Hours
          </Button>
        )}
        {correctionMode && (
          <Button
            variant="text"
            size="small"
            color="inherit"
            onClick={handleCancelCorrectionMode}
          >
            Cancel Correction
          </Button>
        )}
      </Box>

      {(loading || detailLoading) && (
        <Box display="flex" alignItems="center" gap={1} my={2}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="textSecondary">
            Loading detail...
          </Typography>
        </Box>
      )}

      {error && (
        <Typography color="error" variant="body2">
          {error}
        </Typography>
      )}

      {!loading && !detailLoading && !error && currentData.length === 0 && (
        <Typography color="textSecondary" variant="body2" my={2}>
          No timecards for this week
        </Typography>
      )}

      {!loading && !detailLoading && !error && currentData.length > 0 && (
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: 12,
          }}
        >
          <thead>
            <tr>
              <th
                style={{
                  textAlign: 'left',
                  padding: '4px 10px 4px 0',
                  color: '#64748b',
                  fontWeight: 700,
                  borderBottom: '1px solid #e2e8f0',
                  width: 160,
                }}
              >
                Employee
              </th>
              {weekDays.map((day) => (
                <th
                  key={day.date}
                  style={{
                    textAlign: 'center',
                    padding: '4px 6px',
                    color: '#64748b',
                    fontWeight: 700,
                    borderBottom: '1px solid #e2e8f0',
                    minWidth: 50,
                  }}
                >
                  {day.label}
                </th>
              ))}
              {!correctionMode && (
                <>
                  <th
                    style={{
                      textAlign: 'right',
                      padding: '4px 0 4px 10px',
                      color: '#3b82f6',
                      fontWeight: 700,
                      borderBottom: '1px solid #e2e8f0',
                      minWidth: 70,
                    }}
                  >
                    Field
                  </th>
                  <th
                    style={{
                      textAlign: 'right',
                      padding: '4px 0 4px 10px',
                      color: '#8b5cf6',
                      fontWeight: 700,
                      borderBottom: '1px solid #e2e8f0',
                      minWidth: 70,
                    }}
                  >
                    Shop
                  </th>
                  <th
                    style={{
                      textAlign: 'right',
                      padding: '4px 0 4px 10px',
                      color: '#1e293b',
                      fontWeight: 700,
                      borderBottom: '1px solid #e2e8f0',
                      minWidth: 70,
                    }}
                  >
                    Total
                  </th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {currentData.map((emp) => {
              const selState = getEmployeeSelectionState(emp);
              return (
                <tr key={emp.employee_id}>
                  <td
                    style={{
                      padding: '6px 10px 6px 0',
                      fontWeight: 600,
                      color: '#1e293b',
                      borderBottom: '1px solid #f1f5f9',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                    }}
                  >
                    {correctionMode && (
                      <Checkbox
                        size="small"
                        checked={selState.checked}
                        indeterminate={selState.indeterminate}
                        onChange={() => toggleEmployeeRow(emp)}
                        style={{ padding: 2 }}
                      />
                    )}
                    {emp.firstname} {emp.lastname}
                  </td>
                  {weekDays.map((day) => {
                    const { total, st, ot, dt, timecardRows } = getDayHours(
                      emp,
                      day.date
                    );
                    const isCellSelected =
                      !!selectedCells[`${emp.employee_id}|${day.date}`];

                    return (
                      <td
                        key={day.date}
                        onClick={() =>
                          correctionMode && total > 0
                            ? toggleCell(
                                emp.employee_id,
                                day.date,
                                timecardRows
                              )
                            : null
                        }
                        style={{
                          textAlign: 'center',
                          padding: '6px',
                          borderBottom: '1px solid #f1f5f9',
                          verticalAlign: 'top',
                          cursor:
                            correctionMode && total > 0 ? 'pointer' : 'default',
                          backgroundColor: isCellSelected
                            ? '#fffbeb'
                            : 'transparent',
                          borderRadius: isCellSelected ? 4 : 0,
                          border: isCellSelected
                            ? '1px solid #f59e0b'
                            : '1px solid transparent',
                          transition: 'all 0.15s ease',
                        }}
                      >
                        {total === 0 ? (
                          <span style={{ color: '#cbd5e1' }}>—</span>
                        ) : (
                          <div>
                            <div
                              style={{
                                fontWeight: 700,
                                color: isCellSelected ? '#b45309' : '#1e293b',
                              }}
                            >
                              {formatHours(st)}
                            </div>
                            {ot > 0 && (
                              <div
                                style={{
                                  fontSize: 10,
                                  color: '#f59e0b',
                                  fontWeight: 600,
                                }}
                              >
                                {formatPremiumHours(ot, 'OT')}
                              </div>
                            )}
                            {dt > 0 && (
                              <div
                                style={{
                                  fontSize: 10,
                                  color: '#ef4444',
                                  fontWeight: 600,
                                }}
                              >
                                {formatPremiumHours(dt, 'DT')}
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                    );
                  })}
                  {!correctionMode && (
                    <>
                      <td
                        style={{
                          textAlign: 'right',
                          padding: '6px 0 6px 10px',
                          fontWeight: 700,
                          color: '#3b82f6',
                          borderBottom: '1px solid #f1f5f9',
                        }}
                      >
                        {formatHours(emp.field_hrs_total || 0)}
                      </td>
                      <td
                        style={{
                          textAlign: 'right',
                          padding: '6px 0 6px 10px',
                          fontWeight: 700,
                          color: '#8b5cf6',
                          borderBottom: '1px solid #f1f5f9',
                        }}
                      >
                        {formatHours(emp.shop_hrs_total || 0)}
                      </td>
                      <td
                        style={{
                          textAlign: 'right',
                          padding: '6px 0 6px 10px',
                          fontWeight: 800,
                          color: '#1e293b',
                          borderBottom: '1px solid #f1f5f9',
                        }}
                      >
                        {formatHours(
                          (emp.field_hrs_total || 0) + (emp.shop_hrs_total || 0)
                        )}
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {correctionMode && totalSelectedCellsCount > 0 && (
        <Box
          mt={2}
          p={2}
          bgcolor="#fffbeb"
          border="1px solid #fcd34d"
          borderRadius={2}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="body2" fontWeight="bold" color="#92400e">
            Selected: {totalSelectedCellsCount} day-entries
          </Typography>
          <Button
            variant="contained"
            color="warning"
            disableElevation
            onClick={() => setShowCorrectionModal(true)}
          >
            Move Selected Hours &rarr;
          </Button>
        </Box>
      )}

      {showCorrectionModal && (
        <TimecardCorrectionModal
          open={showCorrectionModal}
          onClose={() => setShowCorrectionModal(false)}
          selectedCells={selectedCells}
          detailedRows={detailedRows}
          projectId={projectId}
          sourcePackageId={packageId}
          token={token}
          onSuccess={handleCorrectionSuccess}
        />
      )}

      {isAdmin && (
        <CorrectionHistorySection
          packageId={packageId}
          packageName={packageName}
          token={token}
          refreshKey={historyRefreshKey + refreshKey}
        />
      )}
    </div>
  );
};

export default PackageLaborDetail;
